import { dataNodeUtils, Tree } from '@/components';
import { formatters, ProjectPathStepDetailsDto, TreeNodeDto } from '@/core';
import {
  PathTemplateDetailsState,
  PathTemplateStepDetailsState,
  ProjectPathDetailsState,
} from '@/redux';
import { array } from '@/utils';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Steps, TreeDataNode } from 'antd';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FormPathTemplate.module.scss';

interface Props {
  value: PathTemplateDetailsState | ProjectPathDetailsState;
  className?: string;
}

const { Step } = Steps;

function StepTitle(props: {
  value: PathTemplateStepDetailsState | ProjectPathStepDetailsDto;
}) {
  const { value } = props;
  const {
    name,
    selectedCategories = array.empty<TreeNodeDto>(),
    groupedTaskData,
  } = value;

  const { t } = useTranslation();

  const treeData = useMemo(
    () =>
      dataNodeUtils.convertFromMany(
        selectedCategories,
        'id',
        'name',
        'children',
        'name',
      ),
    [selectedCategories],
  );

  const [showTasks, setShowTasks] = useState(false);

  const taskTree = useMemo<TreeDataNode[]>(() => {
    if (!groupedTaskData) return [];

    const groupMap = new Map<string, string>();
    groupedTaskData.groups.forEach((g) => {
      groupMap.set(g.id, g.name);
    });

    const groupedNodes: TreeDataNode[] = [];
    const ungroupedNodes: TreeDataNode[] = [];

    for (const task of groupedTaskData.tasks) {
      const labelParts: string[] = [task.name];

      if (task.costEstimate != null) {
        labelParts.push(`(${formatters.money(task.costEstimate)})`);
      }

      const taskNode: TreeDataNode = {
        key: `task-${task.name}-${task.groupId ?? 'ungrouped'}`,
        title: labelParts.join(' '),
        isLeaf: true,
      };

      if (task.groupId && groupMap.has(task.groupId)) {
        let groupNode = groupedNodes.find(
          (g) => g.key === `group-${task.groupId}`,
        );
        if (!groupNode) {
          groupNode = {
            key: `group-${task.groupId}`,
            title: groupMap.get(task.groupId)!,
            children: [],
          };
          groupedNodes.push(groupNode);
        }
        groupNode.children!.push(taskNode);
      } else {
        ungroupedNodes.push(taskNode);
      }
    }

    return [...groupedNodes, ...ungroupedNodes];
  }, [groupedTaskData]);

  return (
    <>
      <div className="pt-2">{name}</div>
      <Tree treeData={treeData} selectable={false} virtual={false} />
      {groupedTaskData && (
        <div>
          <Button
            className={classNames('mb-1', styles.link)}
            type="link"
            icon={showTasks ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setShowTasks((prev) => !prev)}
          >
            {t(`pathTemplates.details.tasks.${showTasks ? 'hide' : 'show'}`)}
          </Button>

          {showTasks && (
            <Tree
              treeData={taskTree}
              virtual={false}
              defaultExpandAll
              style={{ marginTop: 8 }}
              selectable={false}
            />
          )}
        </div>
      )}
    </>
  );
}

export function PathTemplateStepList(props: Props) {
  const { value, className } = props;
  return (
    <Steps className={className} direction="vertical" size="small" current={0}>
      {value.steps.map((step, index) => (
        <Step key={index} status="process" title={<StepTitle value={step} />} />
      ))}
    </Steps>
  );
}
